import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// ВАЖНО: Импорт telegram.ts должен быть первым - он инициализирует
// совместимость Login Widget с WebApp API для веб-версии
import './utils/telegram';
// i18n должен быть инициализирован до App (после telegram - нужен для детекции языка)
import './i18n';
import App from './App.tsx';
import './index.css';
// Font Awesome Icons
import '@fortawesome/fontawesome-free/css/all.min.css';
import { initVersionCheck } from './utils/versionCheck';
import { registerServiceWorker, unregisterServiceWorker } from './utils/serviceWorker';
import { initAmplitude, disableAmplitude } from './utils/amplitude';
import { purgeFunctionalStorageKeys } from './utils/consentAwareStorage';

// Детекция iOS для оптимизации производительности (backdrop-filter и т.д.)
// iOS включает iPhone, iPad, iPod
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad с iOS 13+

if (isIOS) {
  document.documentElement.classList.add('is-ios');
  // Для старых iPhone (до 14) добавляем дополнительный класс
  const match = navigator.userAgent.match(/OS (\d+)_/);
  const iosVersion = match ? parseInt(match[1], 10) : 99;
  if (iosVersion < 15) {
    document.documentElement.classList.add('is-ios-legacy');
  }
}

// Инициализируем Amplitude Analytics с Session Replay.
// На .ru сегменте функция вернётся раньше времени, если пользователь не дал согласия
// на аналитические cookie. Слушатель ниже реагирует на любое изменение согласия:
//   analytics:true  → initAmplitude() (включаем)
//   analytics:false → disableAmplitude() (opt-out, перестаём слать события)
// Также при functional:false вычищаем persisted-ключи стор-настроек (FZ-152).
initAmplitude();
window.addEventListener('cookieConsentChanged', (event) => {
  const detail = (event as CustomEvent).detail as
    | { analytics?: boolean; functional?: boolean }
    | undefined;
  if (detail?.analytics) {
    initAmplitude();
  } else {
    disableAmplitude();
  }
  if (detail && detail.functional === false) {
    purgeFunctionalStorageKeys();
  }
});

// Инициализируем систему проверки версий
initVersionCheck().catch(() => {
  // Ignore initialization errors
});

// Текущая версия SW — держать в синхроне с CACHE_VERSION в public/sw.js
const CURRENT_SW_VERSION = 'v6';

// Kill-switch: сносит устаревшие SW-кэши (v5 и старше).
// До коммита 8f32487 старый SW мог закэшировать HTML-страницу как image
// (баг `cacheFirst` при CSP-ошибках во время миграции на media.starproai.com).
// Такие юзеры видят чёрные квадраты вместо превью, пока не сделают хард-релоад.
async function purgeStaleServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('caches' in window)) return false;

  try {
    const cacheNames = await caches.keys();
    const stale = cacheNames.filter(
      (name) => name.startsWith('star-ai-') && !name.endsWith(`-${CURRENT_SW_VERSION}`)
    );

    if (stale.length === 0) return false;

    console.warn('[SW-Purge] Stale caches found, cleaning:', stale);

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));

    await Promise.all(
      cacheNames.filter((n) => n.startsWith('star-ai-')).map((n) => caches.delete(n))
    );

    console.warn('[SW-Purge] Reloading to apply fresh Service Worker');
    window.location.reload();
    return true;
  } catch (error) {
    console.error('[SW-Purge] Failed:', error);
    return false;
  }
}

// Запускаем ASAP (до window.load), чтобы перезагрузка случилась раньше,
// чем юзер увидит битые тамбнейлы, которые может отдать старый SW.
purgeStaleServiceWorker();

// Очистка старых Service Worker при проблемах
// Если sw.js не найден (404), отменяем регистрацию старого SW
async function cleanupBrokenServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      // Если SW не активен или есть проблемы - отменяем регистрацию
      if (!registration.active) {
        await registration.unregister();
        console.log('[SW] Cleaned up inactive Service Worker');
      }
    }
  } catch (e) {
    // Игнорируем ошибки
  }
}

// Регистрируем Service Worker после загрузки страницы
// (не блокирует рендеринг)
if (typeof window !== 'undefined') {
  window.addEventListener('load', async () => {
    // Сначала очищаем проблемные SW
    await cleanupBrokenServiceWorker();

    registerServiceWorker({
      onSuccess: () => {
        console.log('✅ App ready for offline use');
      },
      onUpdate: () => {
        console.log('🔄 New version available, reload to update');
        // Можно показать уведомление пользователю о новой версии
      },
      onError: async (error) => {
        console.warn('⚠️ Service Worker registration failed:', error.message);
        // При ошибке регистрации - отменяем все старые SW
        await unregisterServiceWorker();
      },
    });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
